/**
 * Superadmin — Device Profile Manager (full management)
 * Per user spec:
 * - IEEE 11073 · HL7 FHIR Device · Vendor plugins
 * - Profils de connexion
 * - Vendor plugins · Connectors par constructeur
 * - Chaque dispositif médical expose un profil (vendor, modèle, transport HL7/IEEE)
 * - L'orchestrateur route les observations vers le bon patient/encounter
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Cpu, Search, Plus, Edit, Trash2, Save, Network, Webhook, GitBranch, Package, Wrench, Activity, FileCheck, Zap, Settings, ArrowRight } from "lucide-react";
import { getMockProfiles, STATUS_TONE, STATUS_LABEL, TRANSPORT_LABEL, type DeviceProfile, type Transport, type DeviceStatus } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/device-profiles-admin")({
  component: DeviceProfileAdmin,
});

interface VendorPlugin {
  vendor: string;
  model: string;
  driverPackage: string;
  driverVersion: string;
  transport: Transport;
  iec11073Spec?: string;
  hl7FhirDeviceType?: string;
  messageFormat: string;
  status: "ACTIVE" | "DEPRECATED" | "BETA";
  registeredAt: string;
}

const MOCK_VENDOR_PLUGINS: VendorPlugin[] = [
  { vendor: "Roche", model: "Cobas e411", driverPackage: "@dikdoc/driver-roche-cobas", driverVersion: "2.3.1", transport: "ASTM", messageFormat: "ASTM E1394 record-based", status: "ACTIVE", registeredAt: "2024-01-15" },
  { vendor: "Siemens", model: "Atellica IM", driverPackage: "@dikdoc/driver-siemens-atellica", driverVersion: "1.8.0", transport: "HL7", hl7FhirDeviceType: "Device/1001", messageFormat: "HL7 v2.5 ORU^R01", status: "ACTIVE", registeredAt: "2024-03-20" },
  { vendor: "Mindray", model: "BC-6800", driverPackage: "@dikdoc/driver-mindray-hematology", driverVersion: "3.0.2", transport: "ASTM", messageFormat: "ASTM E1394 + LIS-2A", status: "ACTIVE", registeredAt: "2024-02-10" },
  { vendor: "Philips", model: "IntelliVue MX800", driverPackage: "@dikdoc/driver-philips-intellivue", driverVersion: "2.1.4", transport: "IEEE_11073", iec11073Spec: "IEEE 11073-10201 DIM + 10101 NOM", messageFormat: "IEEE 11073 binary", status: "ACTIVE", registeredAt: "2024-04-05" },
  { vendor: "GE", model: "CARESCAPE B650", driverPackage: "@dikdoc/driver-ge-carescape", driverVersion: "1.5.0", transport: "IEEE_11073", iec11073Spec: "IEEE 11073-10201", messageFormat: "IEEE 11073 binary", status: "ACTIVE", registeredAt: "2024-04-12" },
  { vendor: "Dräger", model: "Infinity M540", driverPackage: "@dikdoc/driver-draeger-infinity", driverVersion: "2.0.1", transport: "HL7", hl7FhirDeviceType: "Device/2002", messageFormat: "HL7 v2.6 ORU + waves", status: "ACTIVE", registeredAt: "2024-05-08" },
  { vendor: "Medtronic", model: "Guardian Connect", driverPackage: "@dikdoc/driver-medtronic-guardian", driverVersion: "1.2.3", transport: "PROPRIETARY", messageFormat: "Medtronic binary over HTTPS", status: "BETA", registeredAt: "2025-09-15" },
  { vendor: "Dexcom", model: "G6 CGM", driverPackage: "@dikdoc/driver-dexcom-g6", driverVersion: "0.9.7", transport: "PROPRIETARY", messageFormat: "Dexcom Share API", status: "BETA", registeredAt: "2025-11-20" },
  { vendor: "Abbott", model: "FreeStyle Libre 3", driverPackage: "@dikdoc/driver-abbott-libre", driverVersion: "1.1.0", transport: "PROPRIETARY", messageFormat: "Abbott LibreLinkUp API", status: "ACTIVE", registeredAt: "2025-12-10" },
  { vendor: "Fujifilm", model: "FCR Carbon XL", driverPackage: "@dikdoc/driver-fujifilm-fcr", driverVersion: "2.0.5", transport: "DICOM", messageFormat: "DICOM C-STORE / C-FIND", status: "ACTIVE", registeredAt: "2024-06-30" },
  { vendor: "Hologic", model: "Selenia Dimensions", driverPackage: "@dikdoc/driver-hologic-selenia", driverVersion: "1.4.0", transport: "DICOM", messageFormat: "DICOM MG object + SR", status: "ACTIVE", registeredAt: "2024-07-18" },
  { vendor: "Olympus", model: "CV-290", driverPackage: "@dikdoc/driver-olympus-cv290", driverVersion: "1.0.3", transport: "DICOM", messageFormat: "DICOM video + still", status: "ACTIVE", registeredAt: "2024-08-22" },
];

interface OrchestrationRoute {
  id: string;
  driverPackage: string;
  matchRule: string;
  targetResource: "Patient" | "Encounter" | "Observation" | "ServiceRequest";
  fhirMapping: string;
  enabled: boolean;
}

const MOCK_ROUTES: OrchestrationRoute[] = [
  { id: "r-001", driverPackage: "@dikdoc/driver-roche-cobas", matchRule: "message.obx.code IN LOINC('4548-4', '4549-2', '2345-7', '2093-3')", targetResource: "Observation", fhirMapping: "code = LOINC, valueQuantity = obx.value, subject = patient.id, effectiveDateTime = obx.timestamp", enabled: true },
  { id: "r-002", driverPackage: "@dikdoc/driver-philips-intellivue", matchRule: "device.partition == 'ICU' AND metric.type IN ('HR','SpO2','BP','RR')", targetResource: "Observation", fhirMapping: "code = MDC, valueQuantity = numeric, subject = encounter.patient", enabled: true },
  { id: "r-003", driverPackage: "@dikdoc/driver-fujifilm-fcr", matchRule: "modality IN ('MG', 'CR', 'DX')", targetResource: "ServiceRequest", fhirMapping: "ImagingStudy + ServiceRequest.fillerOrderNumber = accession", enabled: true },
  { id: "r-004", driverPackage: "@dikdoc/driver-medtronic-guardian", matchRule: "device.type == 'CGM' AND value.sensor == 'glucose'", targetResource: "Observation", fhirMapping: "GlucoseObservation, subject = patient.id (matched by serial)", enabled: false },
];

function DeviceProfileAdmin() {
  const [activeTab, setActiveTab] = useState("profiles");
  const [profiles, setProfiles] = useState<DeviceProfile[]>(getMockProfiles());
  const [plugins, setPlugins] = useState<VendorPlugin[]>(MOCK_VENDOR_PLUGINS);
  const [routes, setRoutes] = useState<OrchestrationRoute[]>(MOCK_ROUTES);
  const [search, setSearch] = useState("");

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Badge variant="destructive" className="text-[10px]">SUPERADMIN</Badge>
        <span className="text-sm text-muted-foreground">· Profils dispositifs · Vendor plugins · Orchestration</span>
      </div>

      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Settings className="h-5 w-5" /> Device Profile Manager
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          IEEE 11073 · HL7 FHIR Device · Vendor plugins · Profils de connexion · Orchestrateur patient/encounter
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Profils connectés</div>
            <div className="mt-1 text-2xl font-bold">{profiles.length}</div>
            <div className="text-xs text-muted-foreground">{profiles.filter((p) => p.status === "ACTIVE").length} actifs</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Vendor plugins</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{plugins.filter((p) => p.status === "ACTIVE").length}</div>
            <div className="text-xs text-muted-foreground">sur {plugins.length} installés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Routes d'orchestration</div>
            <div className="mt-1 text-2xl font-bold text-emerald-400">{routes.filter((r) => r.enabled).length}</div>
            <div className="text-xs text-muted-foreground">sur {routes.length} configurées</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Transports</div>
            <div className="mt-1 text-2xl font-bold">5</div>
            <div className="text-xs text-muted-foreground">HL7 · ASTM · IEEE 11073 · DICOM · Propriétaire</div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="profiles"><Cpu className="h-3.5 w-3.5 mr-1" /> Profils ({profiles.length})</TabsTrigger>
          <TabsTrigger value="plugins"><Package className="h-3.5 w-3.5 mr-1" /> Vendor Plugins ({plugins.length})</TabsTrigger>
          <TabsTrigger value="routes"><GitBranch className="h-3.5 w-3.5 mr-1" /> Routes ({routes.length})</TabsTrigger>
          <TabsTrigger value="certification"><FileCheck className="h-3.5 w-3.5 mr-1" /> Certification</TabsTrigger>
        </TabsList>

        {/* PROFILES TAB */}
        <TabsContent value="profiles" className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Rechercher (vendor, model, IP)..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" /> Ajouter profil</Button>
          </div>

          <div className="space-y-1.5">
            {profiles
              .filter((p) => {
                if (!search) return true;
                const s = search.toLowerCase();
                return p.vendor.toLowerCase().includes(s) || p.model.toLowerCase().includes(s) || p.host.includes(s);
              })
              .map((p) => (
                <Card key={p.id}>
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Cpu className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium">{p.vendor} {p.model}</span>
                          <Badge variant={STATUS_TONE[p.status]} className="text-[10px]">{STATUS_LABEL[p.status]}</Badge>
                          <Badge variant="outline" className="text-[10px]">{TRANSPORT_LABEL[p.transport]}</Badge>
                          {p.iec11073Conformant && <Badge variant="outline" className="text-[10px]">IEEE 11073</Badge>}
                          <Badge variant="outline" className="text-[10px]">IEC 62304 {p.iec62304Class}</Badge>
                        </div>
                        <div className="mt-1 text-[11px] text-muted-foreground font-mono">
                          {p.host}:{p.port} · FW {p.firmware} · Driver {p.driver} · Mapping {p.mappingVersion}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost"><Edit className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost"><Activity className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        </TabsContent>

        {/* VENDOR PLUGINS TAB */}
        <TabsContent value="plugins" className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Chaque constructeur expose un driver <code className="text-xs">@dikdoc/driver-*</code> qui route vers le bon profil
            </p>
            <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" /> Installer un plugin</Button>
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {plugins.map((p) => (
              <Card key={`${p.vendor}-${p.model}`}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{p.vendor} {p.model}</span>
                    <Badge variant={p.status === "ACTIVE" ? "default" : p.status === "BETA" ? "secondary" : "outline"} className="text-[10px]">{p.status}</Badge>
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono">{p.driverPackage}@{p.driverVersion}</div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    <span className="font-medium">Transport:</span> {TRANSPORT_LABEL[p.transport]}
                    {p.iec11073Spec && <span> · <span className="font-medium">IEEE 11073:</span> {p.iec11073Spec}</span>}
                    {p.hl7FhirDeviceType && <span> · <span className="font-medium">FHIR Device:</span> {p.hl7FhirDeviceType}</span>}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground font-mono truncate">{p.messageFormat}</div>
                  <div className="mt-2 flex items-center gap-1">
                    <Button size="sm" variant="outline"><Activity className="h-3 w-3 mr-1" /> Test</Button>
                    <Button size="sm" variant="outline"><Edit className="h-3 w-3 mr-1" /> Config</Button>
                    <Button size="sm" variant="outline"><FileCheck className="h-3 w-3 mr-1" /> Certifier</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* ROUTES TAB */}
        <TabsContent value="routes" className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              L'orchestrateur route les observations brutes vers le bon patient/encounter via ces règles
            </p>
            <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" /> Nouvelle route</Button>
          </div>
          <div className="space-y-2">
            {routes.map((r) => (
              <Card key={r.id}>
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                    <code className="text-[11px] font-mono">{r.driverPackage}</code>
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <Badge variant="outline"><Network className="h-3 w-3 mr-0.5" />FHIR {r.targetResource}</Badge>
                    <Badge variant={r.enabled ? "default" : "outline"} className="ml-auto text-[10px]">{r.enabled ? "ACTIVE" : "DISABLED"}</Badge>
                  </div>
                  <div className="text-xs">
                    <span className="text-muted-foreground">Match: </span>
                    <code className="bg-white/5 px-1.5 py-0.5 rounded text-[10px]">{r.matchRule}</code>
                  </div>
                  <div className="mt-1 text-xs">
                    <span className="text-muted-foreground">FHIR mapping: </span>
                    <code className="bg-white/5 px-1.5 py-0.5 rounded text-[10px]">{r.fhirMapping}</code>
                  </div>
                  <div className="mt-2 flex items-center gap-1">
                    <Button size="sm" variant="outline">Tester avec un message réel</Button>
                    <Button size="sm" variant="outline"><Edit className="h-3 w-3 mr-1" /> Modifier</Button>
                    <Button size="sm" variant="ghost"><Zap className="h-3 w-3" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* CERTIFICATION TAB */}
        <TabsContent value="certification" className="space-y-2">
          <Card>
            <CardHeader>
              <CardTitle>Certification par device</CardTitle>
              <CardDescription>IEC 62304 (classe A/B/C) · IEC 11073 · ISO 13485</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {profiles.map((p) => (
                  <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-xs">
                    <Cpu className="h-3 w-3 text-muted-foreground" />
                    <span className="font-medium">{p.vendor} {p.model}</span>
                    <Badge variant="outline" className="text-[9px]">IEC 62304 {p.iec62304Class}</Badge>
                    {p.iec11073Conformant ? <Badge variant="secondary" className="text-[9px]">IEEE 11073 ✓</Badge> : <Badge variant="outline" className="text-[9px]">N/A</Badge>}
                    <Badge variant="outline" className="text-[9px]">ISO 13485 ✓</Badge>
                    <span className="ml-auto text-[10px] text-muted-foreground">{p.iec62304Class === "C" ? "Haute criticité" : p.iec62304Class === "B" ? "Criticité moyenne" : "Pas de risque"}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
